import {
  ChangeDetectionStrategy,
  Component,
  computed,
  effect,
  inject,
  input,
  output,
  signal,
  untracked,
  viewChild,
} from '@angular/core';
import { BdShareablesService } from '@bindoc-projects/bindoc-permissions-lib/data';
import {
  ShareableFolderObj,
  ShareableType,
  BdSharePermissionHelper,
} from '@bindoc-projects/resource-domain-lib';
import { BdConfirmDeleteService } from '@bindoc-projects/ui-lib/dialog/ui';
import { ConfirmationService, MenuItem } from 'primeng/api';
import { Breadcrumb } from 'primeng/breadcrumb';
import { Button } from 'primeng/button';
import { ConfirmDialog } from 'primeng/confirmdialog';
import { DialogService, DynamicDialogConfig } from 'primeng/dynamicdialog';
import { forkJoin, take } from 'rxjs';

import { BD_ARCHIVE_SHAREABLE_OPENER } from '../../archive-shareable/archive-shareable.token';
import {
  BD_DANGER_MENU_ITEM_CSS,
  BD_DANGER_MENU_ITEM_STYLE,
} from '../../info-menu/danger-menu-item';
import {
  BdEditLabelsDialogComponent,
  EditLabelsDialogData,
  EditLabelsDialogResult,
} from '../../edit-labels-dialog/edit-labels-dialog.component';
import { BdShareableSelectService } from '../../select/global-search/shareable-select.service';

import { Tabs, TabList, Tab } from 'primeng/tabs';

import {
  BdCreateFolderDialogComponent,
  CreateFolderDialogData,
} from '../create-folder-dialog/create-folder-dialog.component';
import { BdGroupSharedTabComponent } from '../group-shares/group-shared-tab.component';
import { BdGroupSharesService } from '../group-shares/group-shares.service';
import { BdFolderTableComponent } from '../folder-table/folder-table.component';
import { isFolderTree } from '../navigation/workspace-location';
import { BdWorkspaceNavigator } from '../navigation/workspace-navigator';
import {
  BdRenameFolderDialogComponent,
  RenameFolderDialogData,
} from '../rename-folder-dialog/rename-folder-dialog.component';
import { BdGrantDialogComponent } from '../grant-dialog/grant-dialog.component';
import { BdMoveDialogComponent } from '../move-dialog/move-dialog.component';
import { BdPermissionsWithDetailsDialogComponent } from '../permission-detail-dialog/permissions-with-details-dialog.component';
import {
  FolderItem,
  FolderTablePresentation,
  GroupView,
  InlineNotice,
  ItemNavigateEvent,
  NavigationScope,
  SHAREABLE_LIST_BROWSE,
  ShareableListPresentation,
} from '../shareables-overview.models';
import { BdSharedFolderNavigator } from '../shared-folder/shared-folder-navigator';
import { ItemSelectionData, SelectedItem } from '../dialog.models';
import { DIALOG_CONFIG_FOR_FILTERS } from '@bindoc-projects/util-lib/ui';

const LOAD_ERROR: InlineNotice = {
  severity: 'error',
  title: 'Der Ordner konnte nicht geladen werden',
  message:
    'Bitte versuchen Sie es in einem Moment erneut oder kehren Sie später zurück.',
};

/**
 * A 403 on a group scope is not a fault — the group is listed for every member
 * of the company, but only its members may open it. That is a permission
 * boundary with a next step, so it reads as an `info` notice, not a red error.
 */
const GROUP_FORBIDDEN: InlineNotice = {
  severity: 'info',
  title: 'Kein Zugriff auf diese Gruppe',
  message:
    'Sie sind kein Mitglied dieser Gruppe. Wenden Sie sich an eine Administratorin oder einen Administrator Ihres Unternehmens, um Zugriff zu erhalten.',
};

@Component({
  selector: 'bd-shareables-tab',
  changeDetection: ChangeDetectionStrategy.OnPush,
  template: `
    <!--
      One path for the whole scope. In a group it reads Firma › Gruppe › …,
      and the folders after the group are those of the tab on screen: the tree
      of "Ordner & Dateien", or the shared folder opened in "Geteilt".
    -->
    <p-breadcrumb
      class="block mb-3"
      data-test="shareables-breadcrumb"
      [model]="crumbItems()"
      [home]="crumbHome()"
    ></p-breadcrumb>
    <!--
      Group scopes carry a second list: what was shared *with this group*.
      Folders and shares are two different questions about the same level, so
      they get two tabs instead of one merged list (concept C) — see
      BdGroupSharedTabComponent for the overlap rule.
    -->
    @if (showGroupTabs()) {
      <p-tabs
        [value]="activeGroupTab()"
        (valueChange)="onGroupTabChange($any($event))"
        class="mb-3"
      >
        <p-tablist>
          <p-tab value="folders" data-test="group-tab-folders">
            Ordner &amp; Dateien
          </p-tab>
          <p-tab value="shared" data-test="group-tab-shared"> Geteilt </p-tab>
        </p-tablist>
      </p-tabs>
    }
    <!--
      One toolbar between the tabs and the table, holding everything that acts
      on the list below it: the way back out of the folder on the left, the
      management actions on the right. The shared list deliberately has no
      management actions: a grant is not a file, so moving or deleting the
      resource belongs to the list that owns it.

      Hidden as a whole when the tab is embedded as a picker: the bulk buttons
      act on the *internal* selection (which a picker hands to its own confirm
      button instead), and creating folders is not part of choosing something
      that already exists.
    -->
    @if (presentation().showActions) {
      <div
        class="flex items-start justify-between gap-3 mb-3"
        data-test="shareables-context-toolbar"
      >
        <div>
          @if (hasParentFolder() && !isSharedList()) {
            <!--
              Reads as the link it replaces — the table's old parent row — so
              the way back keeps the affordance users already know. Grey text,
              not PrimeNG [link]: that renders brand-orange underlined, which
              the styleguide reserves for targets that leave the app.
            -->
            <p-button
              severity="secondary"
              [text]="true"
              icon="pi pi-arrow-up"
              label="Übergeordneter Ordner"
              data-test="shareables-parent-folder"
              (click)="goToParentFolder()"
            ></p-button>
          }
        </div>
        @if (!isSharedList()) {
          <div class="flex gap-2 shrink-0">
            @if (selectedItems().length > 0) {
              <p-button
                severity="secondary"
                [label]="'Verschieben (' + selectedItems().length + ')'"
                data-test="shareables-move-selected"
                (click)="onMove()"
              ></p-button>
              <p-button
                severity="secondary"
                [label]="'Freigeben (' + selectedItems().length + ')'"
                data-test="shareables-share-selected"
                (click)="onShare()"
              ></p-button>
              <p-button
                severity="secondary"
                [label]="'Archivieren (' + selectedItems().length + ')'"
                data-test="shareables-archive-selected"
                (click)="onArchive()"
              ></p-button>
            }
            <p-button
              severity="secondary"
              label="Neuer Ordner"
              icon="pi pi-folder-plus"
              data-test="shareables-create-folder"
              (click)="onCreateFolder()"
            >
            </p-button>
          </div>
        }
      </div>
    }
    @if (isSharedList()) {
      <bd-group-shared-tab
        [groupId]="currentScope().groupId ?? ''"
        [shareableType]="shareableType()"
        [shareableTypes]="shareableTypes()"
        [showType]="showType()"
        [presentation]="presentation()"
        (itemNavigate)="itemNavigate.emit($event)"
        (changed)="reload()"
      ></bd-group-shared-tab>
    } @else {
      <!--
        "typeFilter" is off in picker mode: the resource picker that embeds this
        tab already offers a type filter of its own in its criteria popover, and
        two filters over the same column would fight each other.
      -->
      <bd-folder-table
        [items]="displayedItems()"
        [loading]="loading()"
        [presentation]="tablePresentation()"
        [selection]="selection()"
        [notice]="notice()"
        [showType]="showType()"
        [typeFilter]="!pickerMode()"
        [sortMode]="'client'"
        [highlightedId]="highlightedItemId()"
        [menuItemsProvider]="menuItemsProvider"
        emptyMessage="Dieser Ordner ist leer"
        (itemClick)="onItemClick($event)"
        (selectionChange)="onTableSelectionChange($event)"
      ></bd-folder-table>
    }
    <p-confirmdialog />
  `,
  styles: [
    `
      :host {
        display: flex;
        flex-direction: column;
        flex: 1 1 auto;
        min-height: 0;
      }

      /*
       * Breadcrumb entries navigate via MenuItem.command (no url/routerLink),
       * so PrimeNG renders them as <a> without an href — which means the
       * browser shows the default cursor instead of signalling that they are
       * clickable. Restore the pointer cursor for every enabled crumb (home
       * icon + path entries). See BDA-5165.
       */
      :host ::ng-deep .p-breadcrumb-home-item .p-breadcrumb-item-link,
      :host
        ::ng-deep
        .p-breadcrumb-item:not(.p-disabled)
        .p-breadcrumb-item-link {
        cursor: pointer;
      }

      /* The company crumb of a group scope is a label, not a link. */
      :host ::ng-deep .bd-shareables-crumb-label .p-breadcrumb-item-link,
      :host ::ng-deep .p-breadcrumb-item-link.bd-shareables-crumb-label {
        cursor: default;
      }
    `,
    BD_DANGER_MENU_ITEM_CSS,
  ],
  imports: [
    Breadcrumb,
    BdFolderTableComponent,
    BdGroupSharedTabComponent,
    Button,
    ConfirmDialog,
    Tabs,
    TabList,
    Tab,
  ],
  providers: [
    ConfirmationService,
    BdConfirmDeleteService,
    // Owned here rather than by the "Geteilt" list, so the breadcrumb above the
    // tabs can carry the shared folder's path (BDA-6454).
    BdSharedFolderNavigator,
  ],
})
export class BdShareablesTabComponent {
  public readonly userName = input<string>('');
  public readonly shareableType = input<ShareableType | undefined>(undefined);
  /**
   * Multi-type filter; takes precedence over {@link shareableType} when set.
   * Folders are always listed regardless — they are the navigation, not content
   * that could be filtered away.
   */
  public readonly shareableTypes = input<readonly ShareableType[] | undefined>(
    undefined,
  );
  /** Show a "Typ" column — useful when more than one shareable type is listed. */
  public readonly showType = input(false);
  /**
   * Browse or pick — see {@link ShareableListPresentation}. `showActions`
   * additionally governs the management action bar (bulk move/share/archive plus
   * "Neuer Ordner"); the table flags go through {@link tablePresentation}.
   */
  public readonly presentation = input<ShareableListPresentation>(
    SHAREABLE_LIST_BROWSE,
  );
  /**
   * Externally controlled, folder-spanning selection. Setting it puts the tab in
   * picker mode: folders stop being selectable, file clicks toggle instead of
   * navigating, and {@link selectionChange} reports the merged selection (see
   * {@link onTableSelectionChange}).
   */
  public readonly selection = input<readonly FolderItem[] | undefined>(
    undefined,
  );
  public readonly itemNavigate = output<ItemNavigateEvent>();
  public readonly selectionChange = output<FolderItem[]>();

  /**
   * Where the workspace is, and the folder it shows. The tab renders it and
   * turns clicks into intents; it never loads a folder or touches the URL —
   * that is how the same tab works on the workspace page and in the picker.
   */
  protected readonly navigator = inject(BdWorkspaceNavigator);

  /** The shared folder open in the group's "Geteilt" tab. */
  protected readonly sharedFolders = inject(BdSharedFolderNavigator);

  /** Picker mode — an owner outside controls the selection. */
  public readonly pickerMode = computed(() => this.selection() !== undefined);

  /**
   * What the table gets, with this tab's two deviations from the host's mode:
   * the checkbox column is always there (browsing needs it for the bulk actions
   * above), and folders are selectable everywhere *except* in picker mode, where
   * they are navigation rather than pickable resources.
   */
  protected readonly tablePresentation = computed<FolderTablePresentation>(
    () => ({
      ...this.presentation(),
      showCheckbox: true,
      folderCheckbox: !this.pickerMode(),
    }),
  );

  public readonly currentScope = computed<NavigationScope>(() => {
    const location = this.navigator.location();
    if (location.tab !== 'group') {
      return { kind: 'user' };
    }
    const names = this.navigator.names();
    return {
      kind: 'group',
      groupId: location.groupId,
      groupName: names.groupName,
      companyName: names.companyName,
    };
  });

  /**
   * Which of the two group lists is on screen. Group scopes only — a user scope
   * has no shares granted *to it*, so it stays on the folder list.
   */
  public readonly activeGroupTab = computed<GroupView>(() => {
    const location = this.navigator.location();
    return location.tab === 'group' ? location.view : 'folders';
  });

  /**
   * Tabs belong to a group scope that is being browsed. A picker embedding is
   * choosing a resource out of the folder tree; offering it a second list of
   * things it cannot pick from would only be in the way.
   */
  public readonly showGroupTabs = computed(
    () =>
      this._isGroupScope() &&
      !this.pickerMode() &&
      this.presentation().showActions,
  );

  /**
   * Whether the shared list is the one on screen. Everything that describes the
   * list — toolbar text, icon, which actions apply — reads this instead of
   * `activeGroupTab` alone, so the toolbar can never claim to label a list the
   * template is not rendering.
   */
  public readonly isSharedList = computed(
    () => this.showGroupTabs() && this.activeGroupTab() === 'shared',
  );

  /**
   * Whether there is a level to go up to. The way back used to be the first row
   * of the table; it belongs with the other navigation, so it sits in the
   * toolbar and the table draws no parent row.
   */
  public readonly hasParentFolder = computed(
    () => this.navigator.crumbs().length > 0,
  );

  /**
   * The breadcrumb's start. A group scope starts at its company — a label
   * only, there is no company page to go to; the user's own scope at home.
   */
  public readonly crumbHome = computed<MenuItem>(() =>
    this._isGroupScope()
      ? {
          label: this.currentScope().companyName ?? '',
          icon: 'pi pi-building',
          styleClass: 'bd-shareables-crumb-label',
        }
      : this._breadcrumbHome,
  );

  /**
   * The path after the start: in a group the group itself, leading back to the
   * top of the list on screen, then that list's folders.
   */
  public readonly crumbItems = computed<MenuItem[]>(() => {
    const path = this.navigator.crumbs().map((entry, index) => ({
      label: entry.name,
      command: () => this.navigator.goTo(index),
    }));
    if (!this._isGroupScope()) {
      return path;
    }
    return [
      {
        label: this.currentScope().groupName ?? '',
        icon: 'pi pi-users',
        command: () => this.navigator.goTo(null),
      },
      ...path,
    ];
  });

  /** The folder on screen — the parent of a new folder or a file's "here". */
  public readonly currentFolderId = computed(
    () => this.navigator.list().folder?.folderId ?? '',
  );
  public readonly currentItems = computed<readonly FolderItem[]>(() =>
    isFolderTree(this.navigator.location()) ? this.navigator.list().items : [],
  );
  public readonly selectedItems = signal<FolderItem[]>([]);
  public readonly loading = computed(
    () => this.navigator.list().status === 'loading',
  );
  /** A failed folder load, as the inline state the styleguide prescribes. */
  public readonly notice = computed<InlineNotice | null>(() => {
    switch (this.navigator.list().status) {
      case 'error':
        return LOAD_ERROR;
      case 'forbidden':
        return GROUP_FORBIDDEN;
      default:
        return null;
    }
  });

  /**
   * Id of the row to flash + scroll into view after "Im Ordner öffnen" landed.
   * Cleared after `_HIGHLIGHT_DURATION_MS` so the highlight is transient.
   */
  public readonly highlightedItemId = this.navigator.highlight;

  private static readonly _HIGHLIGHT_DURATION_MS = 2500;

  /**
   * The rows handed to the table: this folder's children, narrowed to the
   * allowed shareable types. Derived rather than refreshed by hand, so a changed
   * type filter takes effect without waiting for the next folder load.
   */
  public readonly displayedItems = computed<FolderItem[]>(() => {
    const types = this._effectiveTypes();
    const items = this.currentItems();
    if (!types) {
      return [...items];
    }
    // The folder API has no type parameter, so the filter is client-side.
    // Folders always stay: they are how the user reaches the files below.
    return items.filter((i) => i.isFolder || types.includes(i.type));
  });

  public readonly folderTable = viewChild(BdFolderTableComponent);

  private readonly _breadcrumbHome: MenuItem = {
    icon: 'pi pi-home',
    command: () => this.navigator.goTo(null),
  };

  private readonly _isGroupScope = computed(
    () => this.currentScope().kind === 'group' && !!this.currentScope().groupId,
  );

  private readonly _folderOnScreen = computed(
    () => this.navigator.list().folder,
  );

  /** Identity of the scope on screen — tab plus group. */
  private readonly _scopeKey = computed(() => {
    const location = this.navigator.location();
    return location.tab === 'group'
      ? `group:${location.groupId}`
      : location.tab;
  });

  private readonly _resourcesService = inject(BdShareablesService);
  private readonly _groupSharesService = inject(BdGroupSharesService);
  private readonly _dialogService = inject(DialogService);
  private readonly _confirmDeleteService = inject(BdConfirmDeleteService);
  private readonly _confirmationService = inject(ConfirmationService);
  private readonly _selectService = inject(BdShareableSelectService);
  private readonly _archiveOpener = inject(BD_ARCHIVE_SHAREABLE_OPENER, {
    optional: true,
  });

  public readonly menuItemsProvider = (item: FolderItem): MenuItem[] => {
    if (!this.presentation().showActions || item.isRecycleBinItem) {
      return [];
    }
    const allowed = item.allowedPermissions ?? [];
    const highest =
      allowed.length > 0
        ? BdSharePermissionHelper.getHighestPermission(allowed)
        : null;
    const canUpdate =
      highest !== null && BdSharePermissionHelper.includes(highest, 'UPDATE');
    const canDelete =
      highest !== null && BdSharePermissionHelper.includes(highest, 'DELETE');
    const showOpenInFolder =
      !item.isFolder &&
      !!item.parentFolderId &&
      item.parentFolderId !== this.currentFolderId();
    const items: MenuItem[] = [
      {
        label: 'Öffnen',
        icon: 'pi pi-external-link',
        command: () => this._open(item),
      },
      ...(item.isFolder
        ? []
        : [
            {
              label: 'In neuem Tab öffnen',
              icon: 'pi pi-window-maximize',
              command: () =>
                this._selectService.openInNewTab(item.id, item.type),
            },
          ]),
      {
        label: 'Im Ordner öffnen',
        icon: 'pi pi-folder-open',
        visible: showOpenInFolder,
        command: () => this._openInWorkspaceFolder(item),
      },
      {
        label: 'Labels bearbeiten',
        icon: 'pi pi-tag',
        visible: canUpdate,
        command: () => this._editLabels(item),
      },
      {
        label: 'Verschieben',
        icon: 'pi pi-arrow-right',
        command: () => this._moveItem(item),
      },
      ...(item.isFolder
        ? [
            {
              label: 'Unterordner erstellen',
              icon: 'pi pi-folder-plus',
              command: () => this._createSubFolder(item),
            },
            {
              label: 'Umbenennen',
              icon: 'pi pi-pencil',
              visible: canUpdate,
              command: () => this._renameItem(item),
            },
          ]
        : []),
      {
        label: 'Freigeben',
        icon: 'pi pi-share-alt',
        // Sharing requires DELETE: the grant dialog's first step calls the
        // share-preview endpoint, which the backend gates on DELETE. Gating the
        // entry on the weaker UPDATE let UPDATE-only users open a dialog that
        // immediately 403s into a dead end (BDA-5507).
        visible: canDelete,
        command: () => this._shareItem(item),
      },
      {
        label: 'Zugriff',
        icon: 'pi pi-key',
        visible: canUpdate,
        command: () => this._showPermissions(item),
      },
      {
        label: 'Löschen',
        icon: 'pi pi-trash',
        ...BD_DANGER_MENU_ITEM_STYLE,
        visible: canDelete,
        command: () => this._deleteItem(item),
      },
    ];
    return items;
  };

  constructor() {
    // A selection belongs to the folder it was made in.
    effect(() => {
      this._folderOnScreen();
      untracked(() => {
        this.folderTable()?.clearSelection();
        this.selectedItems.set([]);
      });
    });

    // A Typ filter follows the user into sub-folders — it narrows the browse,
    // not one listing. A scope change is a different story: the other scope's
    // types are its own, and an inherited filter would present it as empty.
    effect(() => {
      this._scopeKey();
      untracked(() => this.folderTable()?.clearTypeFilter());
    });

    effect((onCleanup) => {
      const id = this.highlightedItemId();
      if (!id) {
        return;
      }
      const timer = setTimeout(
        () => this.navigator.clearHighlight(id),
        BdShareablesTabComponent._HIGHLIGHT_DURATION_MS,
      );
      onCleanup(() => clearTimeout(timer));
    });
  }

  public reload(): void {
    this.navigator.reload();
    // Sharing, filing and revoking all change what the "Geteilt" tab lists, and
    // that list is served from one cached request — so drop the cache with it.
    this._groupSharesService.invalidate();
  }

  /** The user picked the other group tab. */
  public onGroupTabChange(view: GroupView): void {
    this.navigator.dispatch({ type: 'group-view', view });
  }

  public onItemClick(item: FolderItem): void {
    if (item.isFolder) {
      this.navigator.openFolder({ id: item.id, name: item.name });
      return;
    }
    if (this.pickerMode()) {
      // The table owns the toggle (and the folder-spanning merge behind it), so
      // a file click is the same interaction as ticking its checkbox.
      this.folderTable()?.toggle(item);
      return;
    }
    this.itemNavigate.emit({ id: item.id, type: item.type, name: item.name });
  }

  /**
   * Selection change coming from the table. In picker mode its payload is
   * already the owner's complete, folder-spanning selection — the table merges
   * in the picks it does not list itself — so it only needs handing on. In
   * browse mode the payload *is* this folder's rows, which is exactly what the
   * bulk-action bar above counts.
   */
  public onTableSelectionChange(items: FolderItem[]): void {
    if (this.pickerMode()) {
      this.selectionChange.emit(items);
      return;
    }
    this.selectedItems.set(items);
  }

  /** Navigates one folder level up (to the parent folder or the scope root). */
  public goToParentFolder(): void {
    this.navigator.up();
  }

  public onMove(): void {
    this._openMoveDialog(
      this.selectedItems().map((it) => this._toSelectedItem(it)),
    );
  }

  public onShare(): void {
    this._openGrantDialog(
      this.selectedItems().map((it) => this._toSelectedItem(it)),
    );
  }

  public onArchive(): void {
    const items = this.selectedItems();
    if (items.length === 0) {
      return;
    }
    const isPlural = items.length > 1;
    this._confirmationService.confirm({
      header: isPlural
        ? `${items.length} Elemente archivieren?`
        : 'Element archivieren?',
      message: isPlural
        ? `Bitte bestätigen, ob ${items.length} Elemente archiviert werden sollen.`
        : `Bitte bestätigen, ob "${items[0].name}" archiviert werden soll.`,
      icon: 'pi pi-inbox',
      acceptLabel: 'Ja, archivieren',
      acceptButtonStyleClass: 'p-button-danger',
      rejectLabel: 'Abbrechen',
      rejectButtonStyleClass: 'p-button-secondary',
      accept: () => this._archiveItems(items),
    });
  }

  public onCreateFolder(): void {
    const data: CreateFolderDialogData = {
      parentFolderId: this.currentFolderId(),
    };
    this._dialogService
      .open(BdCreateFolderDialogComponent, { header: 'Neuer Ordner', data })
      .onClose.pipe(take(1))
      .subscribe((result) => {
        if (result) {
          this.reload();
        }
      });
  }

  private _open(item: FolderItem): void {
    this.onItemClick(item);
  }

  /**
   * Opens the folder a search hit or a "recent" row lives in, with its row
   * flashing. Inside a group the scope is known; elsewhere the navigator
   * resolves it, exactly as for the same link opened in a new tab.
   */
  private _openInWorkspaceFolder(item: FolderItem): void {
    if (!item.parentFolderId) {
      return;
    }
    this.navigator.openLink({
      shareableId: item.id,
      groupId: this.currentScope().groupId,
    });
  }

  private _createSubFolder(parent: FolderItem): void {
    const data: CreateFolderDialogData = { parentFolderId: parent.id };
    this._dialogService
      .open(BdCreateFolderDialogComponent, { header: 'Unterordner', data })
      .onClose.pipe(take(1))
      .subscribe((result) => {
        if (result) {
          this.reload();
        }
      });
  }

  private _renameItem(item: FolderItem): void {
    const data: RenameFolderDialogData = {
      folderId: item.id,
      currentName: item.name,
    };
    this._dialogService
      .open(BdRenameFolderDialogComponent, {
        header: 'Ordner umbenennen',
        data,
      })
      .onClose.pipe(take(1))
      .subscribe((result: ShareableFolderObj | null) => {
        if (!result) {
          return;
        }
        // Patch the row in place rather than reloading the whole folder — the
        // renamed folder is a child of the current one, so only its label
        // changed.
        this.navigator.patchItems((items) =>
          items.map((it) =>
            it.id === item.id && it.isFolder
              ? { ...it, name: result.name }
              : it,
          ),
        );
      });
  }

  private _moveItem(item: FolderItem): void {
    this._openMoveDialog([this._toSelectedItem(item)]);
  }

  private _shareItem(item: FolderItem): void {
    this._openGrantDialog([this._toSelectedItem(item)]);
  }

  private _editLabels(item: FolderItem): void {
    const dialogConfig: DynamicDialogConfig<EditLabelsDialogData> = {
      ...DIALOG_CONFIG_FOR_FILTERS,
      header: `Labels für „${item.name}“ bearbeiten`,
      data: {
        shareableType: item.type,
        shareableId: item.id,
        shareableName: item.name,
      },
    };
    this._dialogService
      .open(BdEditLabelsDialogComponent, dialogConfig)
      .onClose.pipe(take(1))
      .subscribe((result: EditLabelsDialogResult | null) => {
        if (!result) {
          return;
        }
        this.navigator.patchItems((items) =>
          items.map((it) =>
            it.id === item.id && it.type === item.type
              ? { ...it, labels: result.labels }
              : it,
          ),
        );
      });
  }

  private _showPermissions(item: FolderItem): void {
    const dialogConfig: DynamicDialogConfig = {
      ...DIALOG_CONFIG_FOR_FILTERS,
      header: 'Zugriff',
      data: {
        shareableType: item.type,
        shareableId: item.id,
        shareableName: item.name,
      },
    };
    this._dialogService.open(
      BdPermissionsWithDetailsDialogComponent,
      dialogConfig,
    );
  }

  private _archiveItems(items: FolderItem[]): void {
    forkJoin(items.map((it) => this._resourcesService.archive(it.type, it.id)))
      .pipe(take(1))
      .subscribe(() => {
        const ids = new Set(items.map((i) => i.id));
        this.navigator.patchItems((curr) => curr.filter((i) => !ids.has(i.id)));
        this.folderTable()?.clearSelection();
        this.selectedItems.set([]);
      });
  }

  private _deleteItem(item: FolderItem): void {
    const opener = this._archiveOpener;
    const confirmed$ =
      item.isFolder || !opener
        ? this._confirmDeleteService.confirm({
            itemType: item.isFolder ? 'Ordner' : item.typeLabel,
            itemName: item.name,
          })
        : opener({
            shareableType: item.type,
            shareableId: item.id,
            shareableName: item.name,
          });
    confirmed$.subscribe((confirmed) => {
      if (!confirmed) {
        return;
      }
      this._resourcesService
        .archive(item.type, item.id)
        .pipe(take(1))
        .subscribe(() => {
          this.navigator.patchItems((items) =>
            items.filter((i) => i.id !== item.id),
          );
        });
    });
  }

  private _openMoveDialog(items: SelectedItem[]): void {
    const dialogConfig: DynamicDialogConfig<ItemSelectionData> = {
      ...DIALOG_CONFIG_FOR_FILTERS,
      header: 'Verschieben',
      data: { items },
    };
    this._dialogService
      .open(BdMoveDialogComponent, dialogConfig)
      .onClose.pipe(take(1))
      .subscribe((result) => {
        if (result) {
          this.reload();
        }
      });
  }

  private _openGrantDialog(items: SelectedItem[]): void {
    const dialogConfig: DynamicDialogConfig<ItemSelectionData> = {
      ...DIALOG_CONFIG_FOR_FILTERS,
      header: 'Freigeben',
      data: { items },
    };
    this._dialogService
      .open(BdGrantDialogComponent, dialogConfig)
      .onClose.pipe(take(1))
      .subscribe((result) => {
        if (result?.granted) {
          this.reload();
        }
      });
  }

  private _toSelectedItem(item: FolderItem): SelectedItem {
    return {
      id: item.id,
      type: item.type,
      displayName: item.name,
      typeLabel: item.typeLabel,
      modifiedDate: item.modifiedDate,
    };
  }

  /**
   * `shareableTypes` (multi-type) takes precedence over the single-type
   * `shareableType` input when both are set.
   */
  private _effectiveTypes(): ShareableType[] | undefined {
    const multi = this.shareableTypes();
    if (multi) {
      return [...multi];
    }
    const single = this.shareableType();
    return single ? [single] : undefined;
  }
}
